<template>
  <page class="application-importer">
    <b-alert class="d-flex flex-column justify-content-center align-items-center mb-3" show variant="warning" v-if="!isEnglishLang">
      {{ $t('status.page-only-in-english') }}
    </b-alert>

    <div class="d-flex flex-column justify-content-center align-items-center">
      <h2>Import</h2>
      <b-card class="card-container mt-3">
        <p>We offer a bulk-import tool to update job applications via your ATS's CSV output</p>
        <p>Upload your CSV file here &amp; this tool will parse it into Job Applications for you to review &amp; import</p>
        <p>Please ensure your CSV matches the format of <a href="https://reallinks1.s3.amazonaws.com/assets/import_example/reallinks_import_template.csv">this sample file</a></p>

        <b-btn class="m-0" variant="primary" @click="() => $refs.importModal.show()">Choose your file</b-btn>
        <input class="d-none" type="file" ref="fileInput" accept=".csv" @change="e => receivedFile(e)"/>
      </b-card>
    </div>

    <b-modal ref="importModal" scrollable no-close-on-esc no-close-on-backdrop size="lg"
      title="Import" body-class="p-0" @shown="modalShown" @hidden="modalHidden">
        <b-card-body class="text-center" v-if="state === 'LOADING' || state === 'PROCESSING'">
          <loading-spinner/>
        </b-card-body>
        <b-card-body class="p-0 text-center" v-else-if="state === 'REVIEW'">
          <b-table striped hover responsive primary-key="_id"
            :fields="columns" :items="rows" :current-page="currentPage" :per-page="perPage">
              <template #head(import)>
                <b-form-checkbox :checked="allSelected" :indeterminate="someSelected" @change="toggleSelectAll"/>
              </template>
              <template #cell(import)="{ index }">
                <b-form-checkbox v-model="selected[index]"/>
              </template>
              <template #cell(action)="{ value }">
                <b-badge v-if="value === 'CREATE'" variant="success">{{ value }}</b-badge>
                <b-badge v-if="value === 'UPDATE'" variant="info">{{ value }}</b-badge>
              </template>
          </b-table>
        </b-card-body>
        <b-card-body class="text-center" v-else-if="state === 'FINISHED'">
          <h2>Finished!</h2>
          <p>You may now close this window</p>
        </b-card-body>
        <template v-slot:modal-footer>
          <div class="flex-grow-1 d-flex justify-content-between" v-if="state === 'REVIEW'">
            <b-pagination class="m-0" v-if="rows.length" v-model="currentPage" :total-rows="rows.length" :per-page="perPage"/>
            <b-button v-if="rows.length" @click="startImport" variant="primary">Import</b-button>
          </div>
          <div class="flex-grow-1 d-flex justify-content-end" v-else-if="state === 'FINISHED'">
            <b-button @click="() => $refs.importModal.hide()">Close</b-button>
          </div>
          <p v-else>&nbsp;</p>
        </template>
    </b-modal>

    <template v-slot:footer><footer-minimal/></template>
  </page>
</template>

<script>
import _chunk from 'lodash/chunk';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Page from '@src/components/Page';
import FooterMinimal from '@src/components/Footer-minimal';
import { mapRoot } from '@/mixins/root';
import { formatErr } from '@/lib/utils';
import { parseCSV } from '@/lib/files';

import ApplicationImporterLookupQuery from '@src/graphql/queries/ApplicationImporterLookup.gql';
import ApplicationImporterCreateMutation from '@src/graphql/mutations/ApplicationImporterCreate.gql';
import ApplicationImporterUpdateMutation from '@src/graphql/mutations/ApplicationImporterUpdate.gql';

const MCKESSON_REQUIRED_FIELDS = [
  'Application ID',
  // 'Candidate ID',
  'Vacancy ID',
  'Application Status (Text)',
  // 'Pharmacy Offer - Start Date',
  'Submission Date',
  // 'Application Source (Exp Hire)',
];

export default {
  components: {
    Page,
    FooterMinimal,
    LoadingSpinner,
  },
  data: () => ({
    state: null,
    columns: [],
    rows: [],
    selected: [],
    currentPage: 1,
    perPage: 100,
  }),
  computed: {
    ...mapRoot([ 'company', 'isEnglishLang' ]),
    allSelected() {
      return this.selected.filter(value => value === true).length === this.rows.length;
    },
    someSelected() {
      const { selected, unselected } = this.selected.reduce((results, value) => {
        if (value) {
          results.selected += 1;
        } else {
          results.unselected += 1;
        }
        return results;
      }, {
        selected: 0,
        unselected: 0,
      });

      return this.rows.length !== selected && this.rows.length !== unselected;
    },
  },
  methods: {
    modalShown() {
      // So to the user we have to trigger a loading state AND THEN ask them for the file
      this.$refs.fileInput.click();
      // Which only looks strange if you click CANCEL, because the browser doesn't tell us you cancelled
      this.state = 'LOADING';
    },
    modalHidden() {
      this.state = null;
    },
    async receivedFile(event) {
      try {
        const file = event.target.files[0];
        if (!file) {
          throw formatErr(new Error('No file was found'), {
            code: 'FILE-NOT-FOUND',
          });
        }

        this.state = 'LOADING';
        let contents = null;

        if (file.name.substr(-4) === '.csv') {
          console.log(file);
          contents = await parseCSV(file);
          console.log(contents);
        } else {
          throw formatErr(new Error('An unknown file type was provided'), {
            code: 'UNKNOWN-FILE-TYPE',
          });
        }

        if (!contents || !Array.isArray(contents.data) || !contents.meta || !Array.isArray(contents.meta.fields)) {
          throw new Error('No file contents were found');
        }

        await this.processMcKessonRows({
          fields: contents.meta.fields,
          data: contents.data,
        });
        // if (this.company.handle === 'mckesson') {
        //   await this.processMcKessonRows({
        //     fields: contents.meta.fields,
        //     data: contents.data,
        //   });
        // } else {
        //   this.columns = contents.meta.fields;
        //   this.rows = contents.data.map(row => {
        //     const { 'Application ID': atsID } = row;
        //     row._id = atsID;
        //     return row;
        //   });
        // }

        this.toggleSelectAll(true);

        this.state = 'REVIEW';
      } catch (err) {
        this.setError(err);
      } finally {
        // Regardless of modal result, clear the fileInput
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = null;
        }
      }
    },
    async processMcKessonRows({ fields, data }) {
      const missingColumns = MCKESSON_REQUIRED_FIELDS.filter(field => !fields.includes(field));
      if (missingColumns.length) {
        throw new Error(`Missing columns: ${missingColumns.join(', ')}`);
      }

      fields = [ 'import', 'action' ];
      fields = fields.concat(MCKESSON_REQUIRED_FIELDS);

      const existingIDs = [];
      data = data.filter(r => r && r['Application ID']).map(row => {
        const { 'Application ID': atsID } = row;
        existingIDs.push(atsID);
        row._id = atsID;
        row.action = 'CREATE';
        return row;
      });

      const existingRecords = [];

      if (existingIDs.length) {
        await Promise.all(_chunk(existingIDs, 5000).map(async chunkedAtsIDs => {
          const { data: existingData, errors: existingErrors } = await this.$apollo.query({
            query: ApplicationImporterLookupQuery,
            variables: { existingAtsIDs: chunkedAtsIDs },
            fetchPolicy: 'network-only',
          });

          if (existingData && existingData.company && Array.isArray(existingData.company.applications)) {
            const existingIDs = data.map(({ 'Application ID': atsID }) => atsID);
            existingData.company.applications.forEach(application => {
              Object.assign(data[existingIDs.indexOf(application.atsID)], {
                action: 'UPDATE',
                application,
              });
            });
          }
          if (existingErrors) {
            console.error(existingErrors);
          }
        }));
      }

      console.log(existingRecords);

      this.columns = fields;

      if (fields.indexOf('Application Source (Exp Hire)')) {
        this.rows = data.filter(({ 'Application Source (Exp Hire)': source, application }) => {
          if (application && application.id) {
            return true;
          } else if (`${source}`.trim().toLowerCase() === 'referral') {
            return true;
          } else {
            return false;
          }
        });
      } else {
        this.rows = data.filter(({ application }) => application && application.id);
      }
    },
    setError(err) {
      this.state = 'ERROR';
      console.error(err);
    },
    // getData({ currentPage, perPage }) {
    //   return this.data.slice(Math.max(0, (perPage * (currentPage - 1))), perPage);
    // },
    toggleSelectAll(value) {
      this.selected = this.rows.map(() => value);
    },
    async startImport() {
      try {
        this.state = 'PROCESSING';

        const { createApplications, updateApplications } = this.rows.reduce((result, row, i) => {
          if (this.selected[i]) {
            if (row.application && row.application.id) {
              result.updateApplications.push({ applicationID: row.application.id, update: buildUpdate(row) });
            } else {
              result.createApplications.push({ create: buildCreate(row) });
            }
          }

          return result;
        }, {
          createApplications: [],
          updateApplications: [],
        });

        console.log({ createApplications, updateApplications });

        if (updateApplications.length) {
          await Promise.all(updateApplications.map(async ({ applicationID, update }) => {
            try {
              await this.$apollo.mutate({
                mutation: ApplicationImporterUpdateMutation,
                variables: { applicationID, update },
              });
            } catch (err) {
              this.$root.onError(err);
              // this.$notifyErr(err, {
              //   prepend: 'Failed to update an application:',
              //   noAutoHide: true,
              // });
            }
          }));
        }

        if (createApplications.length) {
          await Promise.all(createApplications.map(async ({ create }) => {
            try {
              await this.$apollo.mutate({
                mutation: ApplicationImporterCreateMutation,
                variables: { create },
              });
            } catch (err) {
              this.$root.onError(err);
              // this.$notifyErr(err, {
              //   prepend: 'Failed to create an application:',
              //   noAutoHide: true,
              // });
            }
          }));
        }

        await this.$root.captureLog({
          eventName: 'APPLICATION_IMPORT',
          user: { self: true },
          data: [
            { key: 'applicationsCreatedCount', value: `${createApplications.length}` },
            { key: 'applicationsUpdatedCount', value: `${updateApplications.length}` },
          ],
        });

        this.state = 'FINISHED';
      } catch (err) {
        this.$notifyErr(err);
        this.setError(err);
      }
    },
  },
};

function deriveStatus(status) {
  switch (`${status}`.trim().toLowerCase()) {
    case 'CV review'.toLowerCase():
    case 'Submitted to HM/RP'.toLowerCase():
    case 'Talent Bank - Added after CV review complete'.toLowerCase():
    case 'CV Review - complete'.toLowerCase():
      return 'CREATED';

    case '1st Interview - scheduled'.toLowerCase():
    case '2nd Interview - scheduled'.toLowerCase():
    case 'Telephone interview - scheduled'.toLowerCase():
    case '1st Interview - complete'.toLowerCase():
    case 'Assessment centre - scheduled'.toLowerCase():
    case 'Assessment centre - complete'.toLowerCase():
    case 'Telephone interview - selected'.toLowerCase():
    case '1st interview - selected'.toLowerCase():
      return 'INTERVIEW';

    case 'Contract sent'.toLowerCase():
    case 'Contract received'.toLowerCase():
    case 'Offer - accepted'.toLowerCase():
    case 'Offer - ready for offer'.toLowerCase():
    case 'Offer - authorisation approved'.toLowerCase():
      return 'HIRED';

    case 'Application withdrawn - CV review stage'.toLowerCase():
    case 'CV review - rejected'.toLowerCase():
    case 'Telephone interview - no show'.toLowerCase():
    case 'Telephone interview - rejected'.toLowerCase():
    case 'Application withdrawn - telephone interview stage'.toLowerCase():
    case '1st Interview - rejected'.toLowerCase():
    case 'Offer - reneged'.toLowerCase():
    case 'Offer - withdrawn'.toLowerCase():
    case 'Application withdrawn - 3rd interview stage'.toLowerCase():
    case '2nd Interview - rejected'.toLowerCase():
    case 'Application withdrawn - 1st interview stage'.toLowerCase():
    case '1st interview - no show'.toLowerCase():
    case 'Rejected from HM/RP'.toLowerCase():
    case 'Rejected from HM/RP complete'.toLowerCase():
    case 'Duplicate - confirmed'.toLowerCase():
      return 'REJECTED';
  }
}

function buildCreate(row) {
  const {
    'Application ID': atsID,
    'Candidate ID': candidateID,
    'Vacancy ID': jobAtsID,
    'Application Status (Text)': applicationStatus,
    'Pharmacy Offer - Start Date': startDate,
    'First name': firstName,
    'Last name': lastName,
  } = row;

  if (!atsID) {
    throw new Error('Expected a Application ID on every application');
  }

  return {
    job: { atsID: jobAtsID },
    fullName: firstName && lastName ? `${firstName} ${lastName}`.trim() : 'TBC',
    atsID,
    atsData: {
      candidateID,
      applicationStatus,
      startDate,
    },
    status: deriveStatus(applicationStatus),
    source: 'IMPORT',
    termsAgreed: true,
  };
}

function buildUpdate({ application, ...row }) {
  const create = buildCreate(row);
  const update = {};

  if (application.job && application.job.atsID !== create.job.atsID) {
    update.job = create.job;
  }

  if (create.fullName && application.fullName !== create.fullName) {
    update.fullName = create.fullName;
  }

  if (JSON.stringify(application.atsData) !== JSON.stringify(create.atsData)) {
    update.atsData = {
      ...(application.atsData || {}),
      ...(create.atsData || {}),
    };
  }

  if (create.status && application.status !== create.status) {
    update.status = create.status;
  }

  return update;
}
</script>

<style lang="scss">
.page.application-importer {
  .card-container {
    width: 100%;
    max-width: 900px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Recruiter/ApplicationImporter.vue